import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/Nav';

import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();

export default function ActivityLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const token = localStorage.getItem('auth_token');

  const fetchLogs = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (emailFilter) params.append('username', emailFilter);
    if (startTime) params.append('start_time', new Date(startTime).toISOString());
    if (endTime) params.append('end_time', new Date(endTime).toISOString());
    params.append('page', page);
    params.append('limit', 20);

    fetch(`${API_BASE_URL}/activity-logs?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setLogs(data.logs || []);
        setTotalPages(Math.ceil((data.total || 0) / 20));
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch activity logs:', err);
        setLogs([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchLogs();
  }, [page]);

  useEffect(() => {
    document.title = "Activity Logs | Network Intelligence Portal";
  }, []);

  const handleFilterSubmit = () => {
    setPage(1);
    fetchLogs();
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto text-gray-800 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold tracking-tight">Activity Logs</h2>
          <p className="text-sm text-gray-500">Recent actions performed across the system</p>
        </div>

        {/* 🔍 Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <input
            type="text"
            placeholder="Search by email"
            className="border px-3 py-2 rounded text-sm"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
          />
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="border px-3 py-2 rounded text-sm"
          />
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="border px-3 py-2 rounded text-sm"
          />
          <button
            onClick={handleFilterSubmit}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
          >
            Apply Filters
          </button>
        </div>

        {/* 🧾 Logs Table */}
        <div className="bg-white border rounded-lg shadow overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center text-gray-500 animate-pulse">Loading activity logs...</div>
          ) : logs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No activity logs found.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600 uppercase text-[11px] border-b">
                <tr>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">IP Address</th>
                  <th className="px-4 py-3 text-left">User Agent</th>
                  <th className="px-4 py-3 text-left">Action</th>
                  <th className="px-4 py-3 text-left">Timestamp</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {logs.map((log, idx) => (
                  <tr key={idx} className="border-t hover:bg-gray-50 transition duration-75">
                    <td className="px-4 py-2">{log.user_email}</td>
                    <td className="px-4 py-2">{log.ip_address}</td>
                    <td className="px-4 py-2 max-w-[240px] truncate">{log.user_agent}</td>
                    <td className="px-4 py-2">{log.action}</td>
                    <td className="px-4 py-2">{new Date(log.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ⏩ Pagination */}
        <div className="flex justify-between items-center pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            ⬅ Prev
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            Next ➡
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}